/**
 * ProactiveSuggestionBadge Component
 *
 * Notification badge on chat header showing proactive suggestion count.
 * Renders inline suggestion list when expanded.
 * Phase 18: Proactive Suggestions
 *
 * @module ProactiveSuggestionBadge
 */

'use client';

import { memo, useState, useCallback } from 'react';
import { Box, Text, Flex, Button, Badge, IconButton } from '@axon/ui';
import { useTranslations, useLocale } from 'next-intl';
import { LuBell, LuX, LuZap } from 'react-icons/lu';

import { useChatbotContext } from '../providers/ChatbotProvider';
import type { ProactiveSuggestion } from '../types/chatbot.types';
import { isEntityId } from '../utils/chatbot.link-utils';

import { EntityLink } from './EntityLink';

// ============================================================================
// Priority Config
// ============================================================================

const PRIORITY_COLOR: Record<string, string> = {
  critical: 'red',
  high: 'orange',
  medium: 'yellow',
  low: 'blue',
};

// ============================================================================
// Component
// ============================================================================

export const ProactiveSuggestionBadge = memo(function ProactiveSuggestionBadge() {
  const { proactiveSuggestions, dismissSuggestion, sendMessage } = useChatbotContext();
  const t = useTranslations('chatbot');
  const locale = useLocale();
  const [isExpanded, setIsExpanded] = useState(false);

  const count = proactiveSuggestions.length;

  const handleDismiss = useCallback(
    (id: string) => {
      dismissSuggestion(id);
    },
    [dismissSuggestion]
  );

  const handleAction = useCallback(
    async (suggestion: ProactiveSuggestion) => {
      if (suggestion.suggestedAction) {
        await sendMessage(
          `${suggestion.suggestedAction.actionType} ${suggestion.suggestedAction.entityType} ${suggestion.suggestedAction.entityId}`
        );
      }
      dismissSuggestion(suggestion.id);
    },
    [sendMessage, dismissSuggestion]
  );

  if (count === 0) return null;

  return (
    <Box position="relative">
      {/* Badge trigger */}
      <Box position="relative" display="inline-block">
        <IconButton
          aria-label={t('suggestions.title')}
          icon={<LuBell size={16} />}
          size="sm"
          variant="ghost"
          onClick={() => setIsExpanded(!isExpanded)}
        />
        <Box position="absolute" top="-1" right="-1">
          <Badge colorPalette="red" size="sm">
            {count}
          </Badge>
        </Box>
      </Box>

      {/* Dropdown */}
      {isExpanded && (
        <Box
          position="absolute"
          top="100%"
          right="0"
          width="300px"
          maxHeight="250px"
          overflowY="auto"
          bg="bg.panel"
          border="1px solid"
          borderColor="border"
          borderRadius="md"
          boxShadow="lg"
          zIndex={10}
          marginTop="1"
        >
          <Box paddingX="3" paddingY="2" borderBottom="1px solid" borderColor="border">
            <Text fontSize="sm" fontWeight="semibold">
              {t('suggestions.title')}
            </Text>
          </Box>

          {proactiveSuggestions.map((suggestion) => (
            <Box
              key={suggestion.id}
              paddingX="3"
              paddingY="2"
              borderBottom="1px solid"
              borderColor="border"
              _hover={{ bg: 'bg.subtle' }}
            >
              <Flex justify="between" align="start" gap="2">
                <Flex gap="2" align="start" flex="1">
                  <Box
                    color={`${PRIORITY_COLOR[suggestion.priority] || 'blue'}.500`}
                    flexShrink={0}
                    marginTop="0.5"
                  >
                    <LuZap size={14} />
                  </Box>
                  <Box flex="1">
                    <Text fontSize="xs" lineHeight="1.4" lineClamp={2}>
                      {suggestion.message}
                    </Text>
                    {suggestion.suggestedAction && (
                      <Flex marginTop="1" align="center" gap="2">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => void handleAction(suggestion)}
                        >
                          {suggestion.suggestedAction.label}
                        </Button>
                        {isEntityId(suggestion.suggestedAction.entityId) && (
                          <EntityLink entityId={suggestion.suggestedAction.entityId} />
                        )}
                      </Flex>
                    )}
                    <Text fontSize="xs" color="fg.muted" marginTop="0.5">
                      {formatTimeAgo(suggestion.createdAt, locale)}
                    </Text>
                  </Box>
                </Flex>
                <IconButton
                  aria-label={t('suggestions.dismiss')}
                  icon={<LuX size={12} />}
                  size="xs"
                  variant="ghost"
                  onClick={() => handleDismiss(suggestion.id)}
                />
              </Flex>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
});

/**
 * Format ISO date string to relative time (bilingual)
 */
function formatTimeAgo(dateStr: string, locale: string = 'en'): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  const isTr = locale === 'tr';

  if (diffMin < 1) return isTr ? 'az önce' : 'just now';
  if (diffMin < 60) return isTr ? `${diffMin}dk önce` : `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return isTr ? `${diffHours}sa önce` : `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  return isTr ? `${diffDays}g önce` : `${diffDays}d ago`;
}

export default ProactiveSuggestionBadge;
